import { Component, Input, OnInit } from '@angular/core';
import { FormBase } from '../../../_classes/form-base.class';
import { Observable, of } from 'rxjs';
import { SessionService } from '../../../_services/session/session.service';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import { ValidatorService } from '../../../_services/validators/validator.service';
import { InputTextModule } from 'primeng/inputtext';
import { TreatmentsService } from '../../../_services/treatments/treatments.service';
import { TreatmentsCategoriesService } from '../../../_services/treatments/treatments-categories.service';
import { EResources } from '../../../_resources/EResources';
import { InputNumberModule } from 'primeng/inputnumber';
import ITreatmentCategory from '../../../_interfaces/ITreatmentCategory';
import ITreatment from '../../../_interfaces/ITreatment';
import { AdminFormActionsComponent } from '../../admin-ui/admin-form-actions/admin-form-actions.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RouterLink } from '@angular/router';
import { CurrenciesService } from '../../../_services/currencies/currencies.service';

@Component({
  selector: 'app-form-treatment',
  imports: [
    TranslateModule,
    FormsModule,
    ButtonModule,
    SelectModule,
    CommonModule,
    InputTextModule,
    InputNumberModule,
    AdminFormActionsComponent,
    RadioButtonModule,
    RouterLink,
  ],
  templateUrl: './form-treatment.component.html',
  styleUrl: './form-treatment.component.scss',
})
export class FormTreatmentComponent
  extends FormBase<ITreatment>
  implements OnInit
{
  @Input() showIntro = true;
  public saveMode: 'manual' | 'all-clinics' = 'manual';
  public readonly colorPresets = [
    '#2563eb',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#14b8a6',
    '#e11d48',
    '#374151',
  ];
  public treatmentsCategories: ITreatmentCategory[];
  public defaultProductImg = 'assets/images/default/product.png';
  public submitted = false;
  public submitting = false;
  public categoriesLoaded = false;
  public basePriceCurrencyLabel = '';
  private currencySymbolByCode = new Map<string, string>();

  get hasCategories(): boolean {
    return (this.treatmentsCategories?.length || 0) > 0;
  }

  get showMissingCategoryWarning(): boolean {
    return this.mode === 'create' && this.categoriesLoaded && !this.hasCategories;
  }

  constructor(
    public session: SessionService,
    public messageService: MessageService,
    public translate: TranslateService,
    public treatmentsService: TreatmentsService,
    public treatmentsCategoriesService: TreatmentsCategoriesService,
    public validator: ValidatorService,
    private currenciesService: CurrenciesService,
  ) {
    super(session, messageService, translate);
  }

  clear(): void {
    this.saveMode = 'manual';
    this.payload = {
      treatments_categories_id: undefined,
      name: undefined,
      description: undefined,
      color: '#3b82f6',
      duration_minutes: 30,
      basePrice: 0,
    };
  }
  generatePayload(): ITreatment {
    return {
      treatments_categories_id: this.payload.treatments_categories_id,
      name: this.payload.name,
      description: this.payload.description,
      color: this.payload.color,
      duration_minutes: Number(this.payload.duration_minutes || 30),
      basePrice: Number(this.payload.basePrice),
    };
  }
  getResourceName(): string {
    return EResources.TREATMENTS;
  }
  updateFn(payload: ITreatment): Observable<any> {
    return this.treatmentsService.update(this.customers_id, this.id, payload);
  }
  createFn(payload: ITreatment): Observable<any> {
    return this.treatmentsService.create(this.customers_id, payload);
  }
  properties(): string[] {
    return [
      'name',
      'description',
      'duration_minutes',
      'color',
      'treatments_categories_id',
      'basePrice',
    ];
  }
  validate(): Promise<void> {
    return this.validator.treatment(this.payload);
  }

  loadCategories() {
    this.treatmentsCategoriesService.getAll(this.customers_id).subscribe({
      next: (treatments_categories: ITreatmentCategory[]) => {
        this.treatmentsCategories = treatments_categories || [];
        this.categoriesLoaded = true;
        if (this.mode === 'create' && this.hasCategories) {
          this.payload.treatments_categories_id = treatments_categories[0].id;
        }
      },
      error: () => {
        this.treatmentsCategories = [];
        this.categoriesLoaded = true;
      },
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.saveMode = 'manual';
    this.payload.color = this.normalizeColor(this.payload.color || '#3b82f6');
    this.payload.duration_minutes = Number(this.payload.duration_minutes || 30);
    if (this.payload.basePrice === undefined || this.payload.basePrice === null) {
      const firstBasePrice = this.payload.basePrices?.[0]?.price;
      this.payload.basePrice = Number(firstBasePrice ?? 0);
    }
    this.loadBasePriceCurrencyLabel();
    this.loadCategories();
  }

  private loadBasePriceCurrencyLabel(): void {
    this.currenciesService.getAll().subscribe({
      next: (currencies) => {
        this.currencySymbolByCode.clear();
        const currencyCodeById = new Map<number, string>();
        (currencies || []).forEach((currency) => {
          const code = String(currency?.code || '').trim().toUpperCase();
          const symbol = String(currency?.symbol || '').trim();
          if (code && symbol) this.currencySymbolByCode.set(code, symbol);
          if (currency?.id && code) {
            currencyCodeById.set(+currency.id, code);
          }
        });

        const rawCustomerCurrency = String(this.customer?.currency || '')
          .trim()
          .toUpperCase();
        const customerCurrencyId = Number((this.customer as any)?.currencies_id || 0);
        const resolvedCode =
          rawCustomerCurrency || currencyCodeById.get(customerCurrencyId) || '';

        this.basePriceCurrencyLabel =
          this.currencySymbolByCode.get(resolvedCode) || resolvedCode;
      },
      error: () => {
        this.basePriceCurrencyLabel = String(
          this.customer?.currency || '',
        )
          .trim()
          .toUpperCase();
      },
    });
  }

  override async execute(): Promise<void> {
    if (this.showMissingCategoryWarning) {
      this.submitting = false;
      return;
    }

    this.submitted = true;
    this.submitting = true;
    await super.execute();
    const hasValidationErrors = Object.values(this.errors || {}).some((value) => !!value);
    if (hasValidationErrors) {
      this.submitting = false;
    }
  }

  override update(payload: ITreatment): void {
    this.updateFn(payload).subscribe({
      next: () => {
        if (this.clearOnUpdate) {
          this.clear();
        }
        this.updateMessageSuccess();
        this.onUpdate(payload);
        this.submitting = false;
      },
      error: () => {
        this.updateMessageError();
        this.submitting = false;
      },
    });
  }

  override create(payload: ITreatment): void {
    this.createFn(payload)
      .pipe(
        switchMap((created) => {
          const treatment = this.resolveCreatedTreatment(created, payload);
          if (this.saveMode !== 'all-clinics') {
            return of({
              assignment: null,
            });
          }
          if (!treatment?.id) {
            return of({ assignment: null });
          }
          return this.assignToAllClinics(treatment).pipe(map((assignment) => ({ assignment })));
        }),
      )
      .subscribe({
        next: ({ assignment }) => {
          this.clear();
          this.createMessageSuccess();
          this.showAssignmentMessage(assignment);
          this.onCreate();
          this.submitting = false;
        },
        error: () => {
          this.createMessageError();
          this.submitting = false;
        },
      });
  }

  onColorInput(rawValue: unknown): void {
    this.payload.color = this.normalizeColor(rawValue);
  }

  get colorPreview(): string {
    return this.normalizeColor(this.payload.color || '#3b82f6');
  }

  get formTitleKey(): string {
    return this.mode === 'edit' ? 'COMMON.TREATMENTS' : 'ADMIN.TREATMENTS.ADD';
  }

  get formDescriptionKey(): string {
    return 'ADMIN.IA.TREATMENTS.DESCRIPTION';
  }

  selectColorPreset(color: string): void {
    this.onColorInput(color);
  }

  private resolveCreatedTreatment(
    created: unknown,
    fallbackPayload: ITreatment,
  ): ITreatment {
    if (created && typeof created === 'object') {
      const createdObject = created as Record<string, any>;
      const directTreatment = createdObject as ITreatment;
      if (directTreatment.id) {
        return {
          ...fallbackPayload,
          ...directTreatment,
        };
      }

      if (createdObject['treatment']?.id) {
        return {
          ...fallbackPayload,
          ...createdObject['treatment'],
        };
      }
    }

    return fallbackPayload;
  }

  private assignToAllClinics(treatment: ITreatment): Observable<{
    total: number;
    success: number;
    failed: number;
  }> {
    const treatmentsId = +(treatment?.id || 0);
    if (!treatmentsId) {
      return of({ total: 0, success: 0, failed: 0 });
    }

    return this.treatmentsService.assignToAllSites(this.customers_id, treatmentsId).pipe(
      map((result) => ({
        total: +(result?.total || 0),
        success: +(result?.success || 0),
        failed: +(result?.failed || 0),
      })),
      catchError(() => of({ total: 1, success: 0, failed: 1 })),
    );
  }

  private showAssignmentMessage(assignment: {
    total: number;
    success: number;
    failed: number;
  } | null): void {
    if (this.saveMode !== 'all-clinics' || !assignment) {
      return;
    }

    if (!assignment.total) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.NO_CLINICS_SUMMARY'),
        detail: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.NO_CLINICS_DETAIL'),
      });
      return;
    }

    if (assignment.failed === 0) {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.SUCCESS_SUMMARY'),
        detail: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.SUCCESS_DETAIL', {
          success: assignment.success,
          total: assignment.total,
        }),
      });
      return;
    }

    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.PARTIAL_SUMMARY'),
      detail: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.PARTIAL_DETAIL', {
        success: assignment.success,
        total: assignment.total,
      }),
    });
  }

  private normalizeColor(value: unknown): string {
    if (value && typeof value === 'object') {
      const color = value as { r?: number; g?: number; b?: number };
      if (
        Number.isFinite(color.r) &&
        Number.isFinite(color.g) &&
        Number.isFinite(color.b)
      ) {
        const toHex = (channel: number) =>
          Math.max(0, Math.min(255, Math.round(channel)))
            .toString(16)
            .padStart(2, '0');
        return `#${toHex(color.r as number)}${toHex(color.g as number)}${toHex(color.b as number)}`;
      }
    }

    const normalized = `${value || ''}`.trim().replace('#', '');
    if (/^[0-9a-fA-F]{3}$/.test(normalized) || /^[0-9a-fA-F]{6}$/.test(normalized)) {
      return `#${normalized}`;
    }
    return '#3b82f6';
  }
}
